import { apiSlice } from "./http";
import { Tags } from "../enum/tags";
import {
  ICampaignRoulette,
  ICampaignRouletteParams,
  ICreateCampaignRoulette,
  IUpdateCampaignRoulette,
  IProbability,
} from "@/@types/campaign-roulette";
import { transformProbabilitiesForAPI } from "@/pages/crm/campaign-roulette/utils/data-transformers";

export const campaignRouletteService = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getDefaultProbabilities: builder.query<IProbability[], void>({
      query: () => ({
        url: "/campaign-roulette/default-probabilities",
        method: "GET",
      }),
      transformResponse: (response: { message: string; data: IProbability[] }) => {
        return response.data;
      },
      providesTags: [Tags.CAMPAIGN_ROULETTE],
    }),

    getCampaignRoulettes: builder.query<
      {
        message: string;
        data: Record<string, unknown>[];
        total: number;
        pagination: {
          page: number;
          limit: number;
          total: number;
          totalPages: number;
          hasNext: boolean;
          hasPrevious: boolean;
        };
      },
      ICampaignRouletteParams
    >({
      query: ({ meta, search, status, orderBy, campaignName, startDate, endDate, sortBy, sortOrder, includeDeleted, campaignType }) => {
        const perPage = meta.perPage ?? 10;
        const params = new URLSearchParams({
          page: meta.page.toString(),
          limit: perPage.toString(),
        });

        if (search) params.append('search', search);
        if (campaignName) params.append('campaignName', campaignName);
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        if (status) params.append('status', status);
        if (sortBy) params.append('sortBy', sortBy);
        if (sortOrder) params.append('sortOrder', sortOrder);
        if (includeDeleted) params.append('includeDeleted', includeDeleted.toString());
        if (orderBy) params.append('orderBy', orderBy);
        if (campaignType) params.append('campaignType', campaignType);

        return {
          url: `/campaign-roulette?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: [Tags.CAMPAIGN_ROULETTE],
    }),

    searchCampaigns: builder.query<
      { id: number; campaignName: string; status: string; startDate: string; endDate: string }[],
      { q: string; limit?: number; campaignType?: string; status?: string }
    >({
      query: ({ q, limit = 5, campaignType, status }) => {
        const params = new URLSearchParams({
          q,
          limit: limit.toString(),
        });

        if (campaignType) params.append("campaignType", campaignType);
        if (status) params.append("status", status);

        return {
          url: `/campaign-roulette/search?${params.toString()}`,
          method: "GET",
        };
      },
      transformResponse: (response: { message: string; data: Record<string, unknown>[]; total: number }) =>
        response.data.map((item: Record<string, unknown>) => ({
          id: item.id as number,
          campaignName: item.campaignName as string,
          status: item.status as string,
          startDate: item.startDate as string,
          endDate: item.endDate as string,
        })),
      providesTags: [Tags.CAMPAIGN_ROULETTE],
    }),

    getCampaignRouletteById: builder.query<{ message: string; data: Record<string, unknown> }, number>({
      query: (id) => ({
        url: `/campaign-roulette/${id}`,
        method: "GET",
      }),
      providesTags: [Tags.CAMPAIGN_ROULETTE],
    }),

    getCampaignStatus: builder.query<
      { id: number; status: string; isActive: boolean; message: string },
      number
    >({
      query: (id) => ({
        url: `/campaign-roulette/campaign-status/${id}`,
        method: "GET",
      }),
      providesTags: [Tags.CAMPAIGN_ROULETTE],
    }),

    validateCampaignPeriod: builder.query<
      { valid: boolean; message: string },
      { startDate: string; endDate: string; excludeId?: number; campaignType?: string }
    >({
      query: ({ startDate, endDate, excludeId, campaignType }) => {
        const params = new URLSearchParams({
          startDate,
          endDate,
        });

        if (excludeId !== undefined) {
          params.append("excludeId", excludeId.toString());
        }

        if (campaignType) {
          params.append("campaignType", campaignType);
        }

        return {
          url: `/campaign-roulette/validate-period?${params.toString()}`,
          method: "GET",
        };
      },
    }),

    createCampaignRoulette: builder.mutation<
      ICampaignRoulette,
      ICreateCampaignRoulette
    >({
      query: (data) => {
        const formData = new FormData();
        
        formData.append('campaignName', data.campaignName);
        formData.append('startDate', data.startDate);
        formData.append('endDate', data.endDate);

        if (data.campaignType) {
          formData.append('campaignType', data.campaignType);
        }

        const stores = data.stores.map(store => ({
          code: parseInt(store.storeCode),
          name: store.storeName,
          nickname: store.nickname
        }));
        formData.append('stores', JSON.stringify(stores));

        if (data.allStoresSelected !== undefined) {
          formData.append('allStoresSelected', data.allStoresSelected.toString());
        }

        const probabilitiesFormatted = transformProbabilitiesForAPI(data.probabilities);
        formData.append('probabilities', JSON.stringify(probabilitiesFormatted));
        
        if (data.regulations && data.regulations instanceof File) {
          formData.append('regulationFile', data.regulations);
        }
        
        if (data.banner3x4 && data.banner3x4 instanceof File) {
          formData.append('banner3x4', data.banner3x4);
        }
        
        if (data.banner16x9 && data.banner16x9 instanceof File) {
          formData.append('banner16x9', data.banner16x9);
        }

        if (data.linkedCampaignId !== undefined && data.linkedCampaignId !== null) {
          formData.append('linkedCampaignId', data.linkedCampaignId.toString());
        }

        return {
          url: "/campaign-roulette",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: [Tags.CAMPAIGN_ROULETTE],
    }),

    updateCampaignRoulette: builder.mutation<
      ICampaignRoulette,
      { id: number; data: IUpdateCampaignRoulette }
    >({
      query: ({ id, data }) => {
        const formData = new FormData();
        
        if (data.campaignName) formData.append('campaignName', data.campaignName);
        if (data.startDate) formData.append('startDate', data.startDate);
        if (data.endDate) formData.append('endDate', data.endDate);
        if (data.campaignType) formData.append('campaignType', data.campaignType);
        
        if (data.stores) {
          const stores = data.stores.map(store => ({
            code: parseInt(store.storeCode),
            name: store.storeName,
            nickname: store.nickname
          }));
          formData.append('stores', JSON.stringify(stores));
        }
        
        if (data.allStoresSelected !== undefined) {
          formData.append('allStoresSelected', data.allStoresSelected.toString());
        }
        
        if (data.probabilities) {
          const probabilitiesFormatted = transformProbabilitiesForAPI(data.probabilities);
          formData.append('probabilities', JSON.stringify(probabilitiesFormatted));
        }
        
        if (data.regulations && data.regulations instanceof File) {
          formData.append('regulationFile', data.regulations);
        }
        
        if (data.banner3x4 && data.banner3x4 instanceof File) {
          formData.append('banner3x4', data.banner3x4);
        }
        
        if (data.banner16x9 && data.banner16x9 instanceof File) {
          formData.append('banner16x9', data.banner16x9);
        }

        if (data.linkedCampaignId !== undefined) {
          formData.append('linkedCampaignId', data.linkedCampaignId === null ? 'null' : data.linkedCampaignId.toString());
        }

        return {
          url: `/campaign-roulette/${id}`,
          method: "PATCH",
          body: formData,
        };
      },
      invalidatesTags: [Tags.CAMPAIGN_ROULETTE],
    }),

    inactivateCampaignRoulette: builder.mutation<void, number>({
      query: (id) => ({
        url: `/campaign-roulette/${id}`,
        method: "PATCH",
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: 'INACTIVE'
        })
      }),
      invalidatesTags: [Tags.CAMPAIGN_ROULETTE],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetDefaultProbabilitiesQuery,
  useGetCampaignRoulettesQuery,
  useSearchCampaignsQuery,
  useLazySearchCampaignsQuery,
  useGetCampaignRouletteByIdQuery,
  useGetCampaignStatusQuery,
  useLazyValidateCampaignPeriodQuery,
  useCreateCampaignRouletteMutation,
  useUpdateCampaignRouletteMutation,
  useInactivateCampaignRouletteMutation,
} = campaignRouletteService;

export const {
  endpoints: {
    getCampaignRoulettes: { select: selectGetCampaignRoulettes },
  },
} = campaignRouletteService;
